import { useState, useRef } from 'react'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { MultiStepMobileWizard } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/MultiStepMobileWizard'
import { observer } from 'mobx-react-lite'
import { useMobileWizardCreateDoc } from 'components/Wizards/MobileWizards/hooks/useMobileWizardCreateDoc'
import { SubmitStepProps } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/Types'
import { FlowProps, TextValues } from 'components/Wizards/MobileWizards/Types'
import { TopicSelector } from '../../components/TopicSelector/TopicSelector'
import { BlogConfig } from '../../components/BlogConfig/BlogConfig'
import { TitleSelector } from '../../components/TitleSelector/TitleSelector'
import { ContentSettings } from '../../components/ContentSettings/ContentSettings'
import { LinkManager } from '../../components/LinkManager/LinkManager'
import { generateTitles } from 'components/Wizards/generateTitles'
import { generateLinks } from 'components/Wizards/generateLinks'
import { useMutation } from 'react-query'
import { GeneratePrompt } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'

export type Values = Pick<
  TextValues,
  | 'filename'
  | 'project'
  | 'topic'
  | 'tone'
  | 'language'
  | 'brand_kit_id'
  | 'content_type'
  | 'audience'
  | 'blog_length'
  | 'keywords'
  | 'include_emojis'
  | 'links'
  | 'title'
>

export const FlowK1 = observer(({ schema, onClose, initialValues, ...props }: FlowProps<ChannelType>) => {
  const workspaceId = useCurrentWorkspaceId()
  const {
    meta: { channel, title },
  } = schema

  const [titleOptions, setTitleOptions] = useState<string[]>([])
  const [linkOptions, setLinkOptions] = useState<string[]>([])
  const [hasLinkError, setHasLinkError] = useState(false)

  const generateTitlesMutationAbortController = useRef<AbortController | null>(null)
  if (generateTitlesMutationAbortController.current === null)
    generateTitlesMutationAbortController.current = new AbortController()
  const generateTitlesMutation = useMutation(generateTitles)

  const generateLinkOptionsMutationAbortController = useRef<AbortController | null>(null)
  if (generateLinkOptionsMutationAbortController.current === null)
    generateLinkOptionsMutationAbortController.current = new AbortController()
  const generateLinkOptionsMutation = useMutation(generateLinks)

  const generateTitleOptions = async (payload: Values) => {
    setTitleOptions([])
    const prompt: GeneratePrompt = { ...payload, generation_type: ChannelType.BlogPost }
    generateTitlesMutation.mutate(
      {
        payload: {
          prompt,
          workspaceId: workspaceId,
        },
        signal: generateTitlesMutationAbortController.current?.signal,
      },
      {
        onSuccess(data) {
          setTitleOptions(data)
        },
        onError(error) {
          reportError(error)
          showErrorMessageToast('Failed to generate titles. Please try again.')
        },
      },
    )
  }

  const generateLinkOptions = async (payload: Values, clearLinks = false) => {
    if (clearLinks) {
      setLinkOptions([])
    }

    setHasLinkError(false)
    const prompt: GeneratePrompt = { ...payload, generation_type: ChannelType.BlogPost }
    generateLinkOptionsMutation.mutate(
      {
        payload: {
          prompt,
          existingLinks: linkOptions,
          workspaceId,
        },
        signal: generateLinkOptionsMutationAbortController.current?.signal,
      },
      {
        onSuccess(data) {
          setLinkOptions((currentLinks) => [...currentLinks, ...data])
        },
        onError(error) {
          reportError(error)
          setHasLinkError(true)
        },
      },
    )
  }

  const removeLinkOption = (index: number) => {
    setLinkOptions((currentLinks) => currentLinks.filter((_, i) => i !== index))
  }

  const { createDoc } = useMobileWizardCreateDoc(channel, {
    navigateToDocOnComplete: true,
    loadingToast: `Generating your ${title}...`,
  })

  const handleSubmitStep = async ({ values, advance, currentStep, totalSteps }: SubmitStepProps<Values>) => {
    if (currentStep === totalSteps - 1) {
      const docValues = {
        ...values,
        filename: values.filename || `Blog Post - ${values.topic || 'Untitled'}`,
        topic: values.topic || 'TOPIC',
        language: values.language || 'en',
        external_links: values.links?.filter((link) => link !== ''),
      }
      await createDoc(docValues)
    } else {
      if (currentStep === 2) {
        generateTitleOptions(values)
      }
      if (currentStep === 3) {
        generateLinkOptions(values, true)
      }
      advance()
    }
  }

  return (
    <MultiStepMobileWizard
      schema={schema}
      onClose={onClose}
      onSubmitStep={handleSubmitStep}
      initialValues={initialValues}
      {...props}
    >
      <TopicSelector />
      <BlogConfig />
      <ContentSettings />
      <TitleSelector
        titleOptions={titleOptions}
        setTitleOptions={setTitleOptions}
        isGeneratingTitles={generateTitlesMutation.isLoading}
        generateTitleOptions={generateTitleOptions}
      />
      <LinkManager
        linkOptions={linkOptions}
        setLinkOptions={setLinkOptions}
        isGeneratingLinks={generateLinkOptionsMutation.isLoading}
        generateLinkOptions={generateLinkOptions}
        removeLinkOption={removeLinkOption}
        hasLinkError={hasLinkError}
      />
    </MultiStepMobileWizard>
  )
})
